import { useCallback, useEffect, useMemo, useState } from "react";
import { Paperclip, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fmtDate } from "../lib/roles";
import { Empty, Loading, PageHeader, Panel, StatoBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { DateDMY } from "../components/DateDMY";

const range = (l) => l.ora_inizio ? `Il ${fmtDate(l.data_inizio)}, ${l.ora_inizio} - ${l.ora_fine}` : `Dal ${fmtDate(l.data_inizio)} al ${fmtDate(l.data_fine)}`;
const iso = (s) => { const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s || ""); return m ? `${m[3]}-${m[2]}-${m[1]}` : (s || "").slice(0, 10); };
const pending = (l) => !l.stato || l.stato === "in attesa" || l.stato === "in_attesa";
const approved = (l) => l.stato === "approvata" || l.stato === "approvato";
const Box = ({ title, count, children, testid }) => (
  <section data-testid={testid}>
    <h2 className="font-display text-xl font-bold text-[var(--navy)] mb-4 flex items-center gap-2">{title}<span className="tag tag-teal">{count}</span></h2>
    {children}
  </section>
);

function Filters({ f, setF }) {
  return (
    <Panel className="!p-4 mb-4 grid sm:grid-cols-[1fr_180px_180px_auto] gap-3 items-end" data-testid="approved-filters">
      <label className="text-xs text-slate-500 space-y-1"><span>Cognome Nome</span>
        <div className="relative"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" /><Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} className="pl-9" placeholder="Cerca dipendente" data-testid="approved-filter-name" /></div></label>
      <label className="text-xs text-slate-500 space-y-1"><span>Inizio dal</span><DateDMY value={f.dal} onChange={(v) => setF({ ...f, dal: v })} testid="approved-filter-from" /></label>
      <label className="text-xs text-slate-500 space-y-1"><span>Inizio al</span><DateDMY value={f.al} onChange={(v) => setF({ ...f, al: v })} testid="approved-filter-to" /></label>
      <Button variant="ghost" className="rounded-full" onClick={() => setF({ q: "", dal: "", al: "" })} data-testid="approved-filter-reset"><X className="w-4 h-4 mr-1" />Azzera</Button>
    </Panel>
  );
}

export default function AdminLeaves() {
  const [list, setList] = useState(null);
  const [reject, setReject] = useState(null);
  const [nota, setNota] = useState("");
  const [f, setF] = useState({ q: "", dal: "", al: "" });
  const load = useCallback(() => api("/admin/leaves").then((d) => setList(d.leaves || [])).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(e.message); } };

  const openCert = async (id) => {
    try {
      const d = await api(`/leaves/${id}/attachment`);
      const src = d.allegato || d.data || d.url;
      const w = window.open();
      if (w && src) w.document.write(src.startsWith("data:application/pdf") ? `<iframe src="${src}" style="border:0;width:100%;height:100vh"></iframe>` : `<img src="${src}" style="max-width:100%"/>`);
    } catch { toast.error("Impossibile aprire il certificato"); }
  };
  const confirmReject = async () => {
    if (!nota.trim()) return toast.error("Inserisci la motivazione del rifiuto");
    await run(() => api(`/admin/leaves/${reject.id}/validate`, { method: "POST", body: { stato: "rifiutata", nota: nota.trim() } }), "Richiesta rifiutata");
    setReject(null); setNota("");
  };

  const todo = useMemo(() => (list || []).filter(pending).sort((a, b) => iso(a.data_inizio).localeCompare(iso(b.data_inizio))), [list]);
  const done = useMemo(() => {
    const words = f.q.toLowerCase().split(/\s+/).filter(Boolean);
    return (list || []).filter(approved)
      .filter((l) => words.every((w) => (l.user_name || "").toLowerCase().includes(w)))
      .filter((l) => (!f.dal || iso(l.data_inizio) >= f.dal) && (!f.al || iso(l.data_inizio) <= f.al))
      .sort((a, b) => iso(b.data_inizio).localeCompare(iso(a.data_inizio)));
  }, [list, f]);

  if (!list) return <Loading />;
  const card = (l) => (
          <Panel key={l.id} className="!p-5" data-testid={`leave-${l.id}`}>
            <div className="flex flex-wrap justify-between gap-2"><div><p className="font-semibold text-[var(--navy)]">{l.user_name}</p><p className="text-xs text-slate-500">{l.mansione}</p></div>
              <div className="flex gap-2 items-center"><span className="tag tag-teal">{l.tipo}</span><StatoBadge stato={l.stato} /></div></div>
            <p className="text-sm text-slate-600 mt-2">{range(l)}</p>
            {l.motivo && <p className="text-sm text-slate-500 mt-1">{l.motivo}</p>}
            <div className="flex flex-wrap gap-2 mt-4">
              {l.has_allegato && <Button size="sm" variant="outline" className="rounded-full" onClick={() => openCert(l.id)} data-testid={`leave-cert-${l.id}`}><Paperclip className="w-4 h-4 mr-1" />Visualizza certificato</Button>}
              {(!l.stato || l.stato === "in attesa" || l.stato === "in_attesa") && <>
                <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => run(() => api(`/admin/leaves/${l.id}/validate`, { method: "POST", body: { stato: "approvata" } }), "Richiesta approvata")} data-testid={`leave-approve-${l.id}`}>Approva</Button>
                <Button size="sm" variant="outline" className="rounded-full text-[var(--red)]" onClick={() => setReject(l)} data-testid={`leave-reject-${l.id}`}>Rifiuta</Button></>}
              <Button size="sm" variant="ghost" className="rounded-full text-slate-500" onClick={() => window.confirm("Eliminare la richiesta?") && run(() => api(`/admin/leaves/${l.id}`, { method: "DELETE" }), "Richiesta eliminata")} data-testid={`leave-delete-${l.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </Panel>);
  return (
    <div>
      <PageHeader subtitle="Pannello Direttore" title="Approva Ferie e Permessi" />
      <div className="space-y-12">
        <Box title="Ferie, permessi, Legge 104 da autorizzare" count={todo.length} testid="leaves-pending-box">
          {todo.length ? <div className="space-y-3 stagger" data-testid="admin-leaves-list">{todo.map(card)}</div> : <Empty title="Nessuna richiesta da autorizzare" />}
        </Box>
        <Box title="Autorizzati" count={done.length} testid="leaves-approved-box">
          <Filters f={f} setF={setF} />
          {done.length ? <div className="space-y-3" data-testid="admin-leaves-approved-list">{done.map(card)}</div> : <Empty title="Nessuna richiesta autorizzata con questi filtri" />}
        </Box>
      </div>
      <Dialog open={!!reject} onOpenChange={() => setReject(null)}><DialogContent><DialogHeader><DialogTitle>Motivazione del rifiuto</DialogTitle></DialogHeader>
        <Textarea value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Indica il motivo del rifiuto..." data-testid="leave-reject-note-input" />
        <Button onClick={confirmReject} className="rounded-full bg-[var(--red)]" data-testid="leave-reject-confirm">Conferma rifiuto</Button>
      </DialogContent></Dialog>
    </div>
  );
}
